import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Download, CheckCircle2 } from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { subscribeEmail } from "@/utils/leads";
import { trackLead } from "@/utils/tracking";
import { setPageMeta } from "@/utils/seo";

const PDF_URL = (process.env.REACT_APP_LEAD_MAGNET_PDF_URL || "").trim();
const COVER = "/cover-good-mind-for-money.jpg";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function LeadMagnet() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sumber = searchParams.get("sumber") || "langsung";
  const [nama, setNama] = useState("");
  const [email, setEmail] = useState("");
  const [setuju, setSetuju] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      "Good Mind for Money | Panduan Gratis NALURA",
      "Good Mind for Money: panduan PDF gratis berisi tujuh kebiasaan kecil untuk pikiran yang lebih tenang soal uang. Tanpa janji instan.",
      {
        og: {
          title: "Good Mind for Money (Panduan Gratis)",
          description: "Good Mind for Money: panduan PDF gratis dari NALURA berisi tujuh kebiasaan kecil untuk pikiran yang lebih tenang soal uang.",
          image: `${window.location.origin}${COVER}`,
        },
      }
    );
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (nama.trim().length < 2) return setError("Mohon isi nama Anda dulu ya.");
    if (!EMAIL_RE.test(email.trim())) return setError("Hmm, format emailnya belum benar. Contoh: nama@gmail.com");
    if (!setuju) return setError("Centang dulu persetujuannya ya, supaya kami boleh mengirim email ke Anda.");
    setSending(true);
    try {
      await subscribeEmail({ nama: nama.trim(), email: email.trim(), sumber });
      sessionStorage.setItem("nalura_lead_tracked", "1");
      trackLead(sumber);
      navigate("/terima-kasih");
    } catch {
      setFailed(true);
      setError("Gagal mengirim, coba lagi. Jangan khawatir — PDF tetap bisa Anda unduh lewat tombol di bawah.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#FBF7EE] text-[#211B10] min-h-screen" data-testid="lead-magnet-page">
      <Nav />
      <main className="pt-28 md:pt-40 pb-24 max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#A67C1E] mb-6">Panduan Gratis &middot; 7 Kebiasaan Kecil</p>
            <h1 className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              <span className="italic text-[#A67C1E]">Good Mind</span> for Money
            </h1>
            <p className="text-base text-[#211B10]/65 leading-relaxed mb-8">
              Tujuh kebiasaan kecil untuk pikiran yang lebih tenang soal uang
            </p>
            <ul className="space-y-4 mb-10">
              {[
                "Ritual 5 menit pagi untuk menata niat uangmu hari itu",
                "Menata dialog internal yang diam-diam menyabotase keputusan uang",
                "Kebiasaan malam supaya tidak tidur membawa cemas soal uang",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-[#211B10]/75">
                  <CheckCircle2 className="w-4 h-4 text-[#A67C1E] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <form onSubmit={submit} className="space-y-3 max-w-md" data-testid="lead-form">
              <input
                data-testid="lead-name-input"
                value={nama}
                onChange={(e) => setNama(e.target.value)}
                placeholder="Nama panggilanmu"
                className="w-full bg-white border border-[#211B10]/15 px-4 py-3.5 text-sm focus:outline-none focus:border-[#A67C1E] placeholder:text-[#211B10]/35"
              />
              <input
                data-testid="lead-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email kamu"
                inputMode="email"
                className="w-full bg-white border border-[#211B10]/15 px-4 py-3.5 text-sm focus:outline-none focus:border-[#A67C1E] placeholder:text-[#211B10]/35"
              />
              <label className="flex items-start gap-3 text-xs text-[#211B10]/65 leading-relaxed cursor-pointer">
                <input
                  data-testid="lead-consent-checkbox"
                  type="checkbox"
                  checked={setuju}
                  onChange={(e) => setSetuju(e.target.checked)}
                  className="mt-0.5 accent-[#A67C1E]"
                />
                <span>
                  Saya setuju menerima panduan ini dan email berkala dari NALURA tentang kebiasaan, pola pikir uang,
                  dan program yang dikurasi (sebagian berisi tautan affiliate), serta menyetujui{" "}
                  <Link to="/kebijakan-privasi" className="underline text-[#A67C1E]">Kebijakan Privasi</Link>.
                  Saya bisa berhenti berlangganan kapan saja.
                </span>
              </label>
              {error && (
                <p data-testid="lead-error" className="text-xs text-[#9B2C2C] bg-[#9B2C2C]/5 border border-[#9B2C2C]/20 px-4 py-3">{error}</p>
              )}
              <button
                data-testid="lead-submit-button"
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-[#A67C1E] text-[#FBF7EE] font-medium text-sm uppercase tracking-[0.12em] px-8 py-4 hover:bg-[#211B10] transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Kirim Panduan Gratis Saya
              </button>
              {failed && PDF_URL && (
                <a
                  data-testid="lead-fallback-download"
                  href={PDF_URL}
                  download
                  className="inline-flex items-center gap-2 ml-0 sm:ml-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A67C1E] underline"
                >
                  Unduh PDF langsung
                </a>
              )}
              <p className="text-[11px] text-[#211B10]/45 leading-relaxed pt-2" data-testid="lead-smallprint">
                Gratis. Kami tidak membagikan atau menjual emailmu. Panduan ini berisi latihan kebiasaan dan refleksi,
                bukan nasihat keuangan, medis, atau psikologis, dan tidak menjanjikan hasil finansial apa pun.
              </p>
            </form>
          </div>

          <div className="flex justify-center">
            <img
              src={COVER}
              alt="Sampul e-book Good Mind for Money dari NALURA"
              loading="lazy"
              className="w-64 md:w-80 border border-[#211B10]/10 shadow-xl"
              data-testid="lead-cover-image"
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
